/**
 * Client HTTP unique de l'application.
 *
 * Toutes les URL passent par `/api`, réécrit vers le backend par le proxy Vite
 * en développement et par Caddy en production : le code applicatif ne connaît
 * donc qu'une seule base d'URL, quel que soit l'environnement.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`HTTP ${status} — ${detail}`);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query): string {
  const url = `/api${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Les valeurs nulles sont OMISES, pas envoyées vides : `?q=` filtrerait
    // sur la chaîne vide côté backend au lieu de ne pas filtrer.
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;
  const response = await fetch(buildUrl(path, query), {
    method,
    signal,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    // On tente de remonter le `detail` de FastAPI : c'est lui qui porte le
    // message utile ("job introuvable", "quota atteint"), pas le code seul.
    let detail = response.statusText;
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string') detail = payload.detail;
    } catch {
      // Corps non JSON : on garde le statusText.
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
